import type {
  AxiosAdapter,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import axios, { AxiosError } from 'axios';

import { getRuntimeApiBaseUrl } from '#/api/runtime-config';
import { isTauri } from '#/utils/platform';

type PlainHeaders = Record<string, string>;

function toPlainHeaders(headers: AxiosRequestConfig['headers']): PlainHeaders {
  const result: PlainHeaders = {};
  const source = (headers as AxiosHeaders | undefined)?.toJSON?.() ?? headers;

  if (!source) return result;

  for (const [key, value] of Object.entries(
    source as Record<string, unknown>,
  )) {
    if (value === null || value === undefined || value === false) continue;
    result[key] = Array.isArray(value) ? value.join(', ') : String(value);
  }

  return result;
}

function toRequestBody(
  data: unknown,
  headers: PlainHeaders,
): BodyInit | undefined {
  if (data === null || data === undefined) return undefined;

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    delete headers['Content-Type'];
    delete headers['content-type'];
    return data;
  }

  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return data;
  }

  if (data instanceof ArrayBuffer) {
    return data;
  }

  if (ArrayBuffer.isView(data)) {
    const view = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return view.slice().buffer;
  }

  if (
    typeof URLSearchParams !== 'undefined' &&
    data instanceof URLSearchParams
  ) {
    return data;
  }

  if (typeof data === 'string') {
    return data;
  }

  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json;charset=utf-8';
  }

  return JSON.stringify(data);
}

async function readResponseData(
  response: Response,
  responseType?: AxiosRequestConfig['responseType'],
) {
  if (responseType === 'arraybuffer') return response.arrayBuffer();
  if (responseType === 'blob') return response.blob();

  const text = await response.text();
  if (responseType === 'text') return text;

  const contentType = response.headers.get('content-type') || '';
  if (!text) return text;

  if (responseType === 'json' || contentType.includes('json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  return text;
}

export function shouldUseTauriHttpAdapter(): boolean {
  return isTauri();
}

export function createTauriHttpAdapter(): AxiosAdapter {
  return async (config: InternalAxiosRequestConfig) => {
    const baseURL = getRuntimeApiBaseUrl();
    if (!baseURL) {
      throw new AxiosError('Desktop API base URL is not configured');
    }

    const requestUrl = axios.getUri({
      ...config,
      baseURL,
    });

    const headers = toPlainHeaders(config.headers);
    const method = (config.method || 'get').toUpperCase();
    const body = ['GET', 'HEAD'].includes(method)
      ? undefined
      : toRequestBody(config.data, headers);

    try {
      const { fetch } = await import('@tauri-apps/plugin-http');
      const response = await fetch(requestUrl, {
        body,
        connectTimeout: config.timeout,
        headers,
        method,
        signal: config.signal as AbortSignal | undefined,
      });

      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      const data = await readResponseData(response, config.responseType);
      const axiosResponse: AxiosResponse = {
        config,
        data,
        headers: responseHeaders,
        request: undefined,
        status: response.status,
        statusText: response.statusText,
      };

      const validateStatus =
        config.validateStatus ||
        ((status: number) => status >= 200 && status < 300);

      if (!validateStatus(response.status)) {
        throw new AxiosError(
          `Request failed with status code ${response.status}`,
          undefined,
          config,
          undefined,
          axiosResponse,
        );
      }

      return axiosResponse;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw error;
      }

      throw new AxiosError(
        error instanceof Error ? error.message : 'Network Error',
        'ERR_NETWORK',
        config,
      );
    }
  };
}
